import { Injectable, signal, WritableSignal } from '@angular/core';

/**
 * Service to manage global page loading state.
 * Tracks both HTTP requests and image loading to provide seamless UX.
 */
@Injectable({
  providedIn: 'root',
})
export class PageLoaderService {
  private readonly _isLoading: WritableSignal<boolean> = signal(false);
  private requestCount = 0;
  private imageCount = 0;
  private updateTimeout?: ReturnType<typeof setTimeout>;

  /** Observable loading state */
  public readonly isLoading = this._isLoading.asReadonly();

  /** Show loader for HTTP request */
  public show(): void {
    this.requestCount = Math.max(0, this.requestCount + 1);
    this._scheduleUpdate();
  }

  /** Hide loader for HTTP request */
  public hide(): void {
    this.requestCount = Math.max(0, this.requestCount - 1);
    this._scheduleUpdate();
  }

  /** Show loader for image loading */
  public showForImage(): void {
    this.imageCount = Math.max(0, this.imageCount + 1);
    this._scheduleUpdate();
  }

  /** Hide loader for image loading */
  public hideForImage(): void {
    this.imageCount = Math.max(0, this.imageCount - 1);
    this._scheduleUpdate();
  }

  /** Force hide loader and reset all counters */
  public forceHide(): void {
    this.requestCount = 0;
    this.imageCount = 0;
    this._cancelScheduledUpdate();
    this._isLoading.set(false);
  }

  /** Schedule loading state update with debouncing */
  private _scheduleUpdate(): void {
    this._cancelScheduledUpdate();
    
    // Immediate show, debounced hide for smooth UX
    const shouldShow = this.requestCount > 0 || this.imageCount > 0;
    
    if (shouldShow) {
      this._isLoading.set(true);
    } else {
      // Delay hiding to prevent flickering
      this.updateTimeout = setTimeout(() => {
        this._isLoading.set(false);
      }, 100);
    }
  }

  /** Cancel scheduled update */
  private _cancelScheduledUpdate(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = undefined;
    }
  }
}